import { html, nothing } from 'lit';

import { getEnvDotColor } from '../../utils/get-env-dot-color';
import type { SolutionChannelGroup } from '../../utils/group-channels-by-solution';
import type { PlatformChannel } from '../../utils/map-connection-to-row';
import {
  buildSolutionGroupViews,
  type SolutionGroupsTestIdPrefix,
  type SolutionGroupsTranslate,
} from './shared';
import { SOLUTION_CHANNEL_GROUPS_CSS } from './styles';

export interface RenderSolutionChannelGroupsArgs {
  groups: SolutionChannelGroup[];
  /** Scopes the expanded state, see `groupToggleKey`. */
  ownerId: string;
  isExpanded: (key: string) => boolean;
  onToggle: (key: string) => void;
  onGoToChannel: (channel: PlatformChannel) => void;
  onGoToSolution: (solutionId: string) => void;
  testIdPrefix: SolutionGroupsTestIdPrefix;
  /** Plain function, not a hook: this renderer runs inside a table cell. */
  translate: SolutionGroupsTranslate;
}

/**
 * The keys `ps-data-table`'s row treats as "select this row": it calls
 * `preventDefault()` + `toggleSelected()` on a keydown of either, for ANY
 * target that bubbled up from a cell (`data-table.wc.ts`, the `ps-table-row`
 * keydown handler). Mirrors that check, `e.code === 'Space'` included.
 */
const isRowActivationKey = (e: KeyboardEvent) =>
  e.key === 'Enter' || e.key === ' ' || e.code === 'Space';

/**
 * Keeps a button's own activation keys away from the row. Without it the
 * row's `preventDefault()` cancels the button's native activation (so Enter /
 * Space do nothing) and toggles the row selection instead. Only these two keys
 * are stopped: Escape and the rest must still reach the document-level
 * handlers of an open popover.
 */
const stopRowKeyActivation = (e: KeyboardEvent) => {
  if (isRowActivationKey(e)) e.stopPropagation();
};

/** A click on our button is the button's, never the row's selection toggle. */
const ownClick = (action: () => void) => (e: Event) => {
  e.stopPropagation();
  action();
};

/**
 * Lit renderer of the solution boxes for the table cell. The React twin used by
 * the Edit modal is `SolutionChannelGroups`; both map the same view model
 * (`buildSolutionGroupViews`) and inject the same stylesheet. Only this one
 * stops propagation, because only it renders inside a `ps-data-table` row.
 */
export const renderSolutionChannelGroups = ({
  groups,
  ownerId,
  isExpanded,
  onToggle,
  onGoToChannel,
  onGoToSolution,
  testIdPrefix,
  translate,
}: RenderSolutionChannelGroupsArgs) => html`
  <style>
    ${SOLUTION_CHANNEL_GROUPS_CSS}
  </style>
  ${buildSolutionGroupViews({
    groups,
    ownerId,
    isExpanded,
    testIdPrefix,
    translate,
  }).map(
    ({ link, ...view }) =>
      html`<div
        class="c-solution-group"
        role=${view.regionLabel == null ? nothing : 'region'}
        aria-label=${view.regionLabel ?? nothing}
        test-id=${view.testId}
      >
        ${view.unresolved
          ? nothing
          : link
            ? html`<ps-tooltip content=${link.tooltip}>
                <button
                  type="button"
                  class="c-solution-group__chip"
                  aria-label=${link.ariaLabel}
                  @keydown=${stopRowKeyActivation}
                  @click=${ownClick(() => onGoToSolution(link.solutionId))}
                >
                  <span class="c-solution-group__label" title=${view.name}
                    >${view.name}</span
                  >
                  <ps-icon name="arrow-in-circle" size="xsmall"></ps-icon>
                </button>
              </ps-tooltip>`
            : html`<span
                class="c-solution-group__chip c-solution-group__chip--static"
              >
                <span class="c-solution-group__label" title=${view.name}
                  >${view.name}</span
                >
              </span>`}
        ${view.pills.map((pill) => {
          const content = html`<span
              class="c-solution-group__dot"
              style=${`background:${getEnvDotColor(pill.channel.env)}`}
            ></span>
            <span class="c-solution-group__label" title=${pill.name}
              >${pill.name}</span
            >`;
          // A pill that leads nowhere is text, named by its visible name: no
          // button, tooltip or arrow promising a page (see ChannelPillView).
          if (!pill.interactive) {
            return html`<span
              class="c-solution-group__pill c-solution-group__pill--static"
              test-id=${pill.testId}
              >${content}</span
            >`;
          }
          return html`<ps-tooltip content=${pill.tooltip}>
            <button
              type="button"
              class="c-solution-group__pill"
              test-id=${pill.testId}
              aria-label=${pill.ariaLabel}
              @keydown=${stopRowKeyActivation}
              @click=${ownClick(() => onGoToChannel(pill.channel))}
            >
              ${content}
              <ps-icon name="arrow-in-circle" size="xsmall"></ps-icon>
            </button>
          </ps-tooltip>`;
        })}
        ${view.more
          ? html`<button
              type="button"
              class="c-solution-group__more"
              aria-expanded=${view.more.expanded ? 'true' : 'false'}
              @keydown=${stopRowKeyActivation}
              @click=${ownClick(() => onToggle(view.key))}
            >
              ${view.more.label}
              <ps-icon name=${view.more.icon} size="xsmall"></ps-icon>
            </button>`
          : nothing}
      </div>`
  )}
`;
